import { ColorTranslator } from 'colortranslator';

export type ThemeColors = {
    primary?: string | null;
    secondary?: string | null;
    navBackground?: string | null;
    navColor?: string | null;
    menuColor?: string | null;
};

export const themeColorProperties = [
    '--color-accent',
    '--color-accent-solid',
    '--color-primary',
    '--color-accent-fg',
    '--color-accent-soft',
    '--color-accent-strong',
    '--color-accent-strong-dark',
    '--color-side-bg',
    '--color-side-bg-dark',
    '--color-side-fg',
    '--color-side-fg-dark',
    '--color-side-muted',
    '--color-side-muted-dark',
    '--color-side-hover',
    '--color-side-hover-dark',
    '--color-side-active',
    '--color-side-active-dark',
    '--color-side-border',
    '--color-side-border-dark',
    '--color-side-active-fg',
    '--color-side-active-accent'
] as const;

function asHex(value?: string | null)
{
    if (!value?.trim())
        return '';

    try
    {
        return new ColorTranslator(value).HEX;
    }
    catch
    {
        return '';
    }
}

function channels(color: string)
{
    return color.slice(1).match(/.{2}/g)?.slice(0, 3).map(part => parseInt(part, 16)) ?? [0, 0, 0];
}

function luminance(color: string)
{
    const rgb = channels(color).map(value =>
    {
        const channel = value / 255;

        return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function contrastRatio(first: string, second: string)
{
    const values = [luminance(first), luminance(second)].sort((a, b) => b - a);

    return (values[0] + 0.05) / (values[1] + 0.05);
}

function readableText(background: string)
{
    return contrastRatio('#000000', background) >= contrastRatio('#ffffff', background) ? '#000000' : '#ffffff';
}

function mixHex(first: string, second: string, part: number)
{
    const left = channels(first);
    const right = channels(second);
    const mixed = left.map((value, index) => Math.round(value * (1 - part) + right[index] * part));

    return `#${mixed.map(value => value.toString(16).padStart(2, '0')).join('')}`;
}

function solidAccent(background: string)
{
    if (luminance(background) > 0.26 || contrastRatio('#ffffff', background) >= 4.5)
        return background;

    for (let step = 1; step <= 100; step++)
    {
        const candidate = mixHex(background, '#000000', step / 100);

        if (contrastRatio('#ffffff', candidate) >= 4.5)
            return candidate;
    }

    return '#000000';
}

function accessibleText(color: string, background: string)
{
    if (contrastRatio(color, background) >= 4.5)
        return color;

    const target = readableText(background);

    for (let step = 1; step <= 20; step++)
    {
        const candidate = mixHex(color, target, step / 20);

        if (contrastRatio(candidate, background) >= 4.5)
            return candidate;
    }

    return target;
}

export function themeColorOverrides(colors: ThemeColors): Record<string, string>
{
    const overrides: Record<string, string> = {};
    const primary = asHex(colors.primary);
    const secondary = asHex(colors.secondary);
    const navBackground = asHex(colors.navBackground);
    const navColor = asHex(colors.navColor);
    const menuColor = asHex(colors.menuColor);

    if (primary)
    {
        overrides['--color-accent'] = primary;
        overrides['--color-accent-solid'] = solidAccent(primary);
        overrides['--color-primary'] = primary;
        overrides['--color-accent-fg'] = luminance(primary) <= 0.26 ? '#ffffff' : '#000000';
        overrides['--color-accent-soft'] = `color-mix(in srgb, ${primary} 14%, white)`;
        overrides['--color-accent-strong'] = secondary ? accessibleText(secondary, '#ffffff') : accessibleText(primary, '#ffffff');
        overrides['--color-accent-strong-dark'] = accessibleText(secondary || primary, '#2b2c2f');
    }
    else if (secondary)
    {
        overrides['--color-accent-strong'] = accessibleText(secondary, '#ffffff');
        overrides['--color-accent-strong-dark'] = accessibleText(secondary, '#2b2c2f');
    }

    if (navBackground || navColor)
    {
        const background = navBackground || '#1d1d20';
        const foreground = navColor ? accessibleText(navColor, background) : readableText(background);

        if (navBackground)
        {
            overrides['--color-side-bg'] = background;
            overrides['--color-side-bg-dark'] = background;
        }

        overrides['--color-side-fg'] = foreground;
        overrides['--color-side-fg-dark'] = foreground;
        overrides['--color-side-muted'] = `color-mix(in srgb, ${foreground} 78%, ${background})`;
        overrides['--color-side-muted-dark'] = overrides['--color-side-muted'];
        overrides['--color-side-hover'] = `color-mix(in srgb, ${foreground} 12%, ${background})`;
        overrides['--color-side-hover-dark'] = overrides['--color-side-hover'];
        overrides['--color-side-border'] = `color-mix(in srgb, ${foreground} 18%, ${background})`;
        overrides['--color-side-border-dark'] = overrides['--color-side-border'];
    }

    if (primary || navBackground)
    {
        const background = navBackground || '#1d1d20';

        overrides['--color-side-active-accent'] = accessibleText(primary || '#ffb900', background);
    }

    if (menuColor)
    {
        const background = navBackground || '#1d1d20';
        const accent = primary || '#ffb900';
        const activeBackground = mixHex(background, accent, 0.18);
        const activeColor = accessibleText(menuColor, activeBackground);

        overrides['--color-side-active-fg'] = activeColor;
        overrides['--color-side-active-accent'] = activeColor;
    }

    return overrides;
}

export function applyThemeColors(style: CSSStyleDeclaration, colors: ThemeColors)
{
    const overrides = themeColorOverrides(colors);

    for (const property of themeColorProperties)
    {
        if (overrides[property])
            style.setProperty(property, overrides[property]);
        else
            style.removeProperty(property);
    }
}
